import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ModalTransition } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable.tsx";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { defaultPageSize } from "src/config/table-config.ts";
import { CaseVersionsDiffTable } from "src/features/case/CaseVersionsDiff/CaseVersionsDiffTable";
import { VersionConfig } from "src/features/case/CaseVersionsDiff/CaseVersionsDiffTable.types";
import { useFormatDate, useT } from "src/internationalization";
import { AlertSummary, ShortAlert } from "src/types/alert";
import { CaseSummary } from "src/types/case";
import { ScenarioExecutionResultWithCase } from "src/types/scenario";

import { AlertsAndExecutionsToolbar } from "./AlertsAndExecutionsToolbar";
import { CompareExecutionsModal } from "./CompareExecutionsModal";
import { useAlertsAndExecutionsTabData } from "./useAlertsAndExecutionsTabData";

export type AlertsAndExecutionsTabProps = {
  setIsDisplayExecutionButton: (isDisplayExecutionButton: boolean) => void;
  executionResults?: ScenarioExecutionResultWithCase[];
  alerts?: (ShortAlert | AlertSummary)[];
  selectedCase?: {
    id: string;
    request: string | null;
  };
  selectedAlert?: {
    id: string;
  };
};

const AlertsAndExecutionsTab: React.FC<AlertsAndExecutionsTabProps> = ({
  setIsDisplayExecutionButton,
  executionResults,
  selectedCase,
  alerts,
  selectedAlert,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const formatDate = useFormatDate();
  const { showErrorToast } = useToast();
  const [compareExecutionsVersions, setCompareExecutionsVersions] = useState<
    VersionConfig[] | null
  >(null);
  const [compareExecutionsModal, setCompareExecutionsModal] = useState<{
    firstExecution: ScenarioExecutionResultWithCase;
    secondExecution: ScenarioExecutionResultWithCase;
  } | null>(null);
  const onlyOpenAlerts = searchParams.get("onlyOpenAlerts") === "true";

  const setOnlyOpenAlerts = (value: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("onlyOpenAlerts", "true");
    } else {
      next.delete("onlyOpenAlerts");
    }
    setSearchParams(next);
  };

  const { data, columns } = useAlertsAndExecutionsTabData({
    executionResults,
    alerts,
    selectedCase,
    selectedAlert,
  });

  const executionResultsMap = useMemo(() => {
    const map = new Map<string, ScenarioExecutionResultWithCase>();
    executionResults?.forEach((result) => {
      map.set(result.id, result);
    });
    return map;
  }, [executionResults]);

  const alertsMap = useMemo(() => {
    const map = new Map<string, AlertSummary>();
    alerts?.forEach((alert) => {
      // Only include alerts that have creationCase (AlertSummary)
      if ("creationCase" in alert) {
        map.set(alert.id, alert);
      }
    });
    return map;
  }, [alerts]);

  const alertsById = useMemo(() => {
    const map = new Map<string, ShortAlert | AlertSummary>();
    alerts?.forEach((alert) => map.set(alert.id, alert));
    return map;
  }, [alerts]);

  const filteredData = useMemo(() => {
    if (!onlyOpenAlerts) return data;
    return data.filter((row) => {
      let alert: ShortAlert | AlertSummary | undefined;
      if (row.scenario === "Manual") {
        alert = alertsById.get(row.id);
      } else {
        const execution = executionResultsMap.get(row.id);
        const alertId = execution?.alert?.id;
        if (alertId) alert = alertsById.get(alertId);
      }
      if (!alert) return false;
      return alert.status !== "closed" && alert.status !== "canceled";
    });
  }, [data, onlyOpenAlerts, alertsById, executionResultsMap]);

  const handleCompareExecutions = (selectedIndexes: number[]) => {
    if (selectedIndexes.length !== 2) return;

    const selectedItems = selectedIndexes.map((index) => filteredData[index]);

    const itemsWithSnapshots: {
      id: string;
      creationCase: CaseSummary;
      date: Date;
      scenarioName?: string;
      isManualAlert: boolean;
    }[] = [];

    for (const item of selectedItems) {
      if (item.scenario === "Manual") {
        const alert = alertsMap.get(item.id);
        if (alert?.creationCase) {
          itemsWithSnapshots.push({
            id: alert.id,
            creationCase: alert.creationCase,
            date: alert.creationCase.snapshotCreation || alert.adminStatus.creation,
            isManualAlert: true,
          });
        }
      } else {
        const execution = executionResultsMap.get(item.id);
        if (execution?.creationCase) {
          itemsWithSnapshots.push({
            id: execution.id,
            creationCase: execution.creationCase,
            date: execution.creationCase.snapshotCreation || execution.date,
            scenarioName: execution.scenarioVersion?.scenario?.name,
            isManualAlert: false,
          });
        }
      }
    }

    if (itemsWithSnapshots.length !== 2) return;

    const [item1, item2] = itemsWithSnapshots;

    const createCustomHeader = (
      scenarioName: string | undefined,
      isManualAlert: boolean,
      date: Date
    ): React.ReactNode => {
      const dateTimeLabel = formatDate(date, {
        dateStyle: "long",
        timeStyle: "short",
      });

      if (isManualAlert) {
        return (
          <div className="flex flex-col">
            <span>{t("caseVersions.diff.executions.manual.alert")}</span>
            <span className="text-sm text-gray-500">{dateTimeLabel}</span>
          </div>
        );
      } else if (scenarioName) {
        return (
          <div className="flex flex-col">
            <span>{scenarioName}</span>
            <span className="text-sm text-gray-500">{dateTimeLabel}</span>
          </div>
        );
      }
      return dateTimeLabel;
    };

    const versions: VersionConfig[] = [
      {
        key: "item2-creation",
        case: item2.creationCase,
        label: "caseVersions.diff.table.scored",
        date: item2.date,
        customHeader: createCustomHeader(item2.scenarioName, item2.isManualAlert, item2.date),
      },
      {
        key: "item1-creation",
        case: item1.creationCase,
        label: "caseVersions.diff.table.scored",
        date: item1.date,
        customHeader: createCustomHeader(item1.scenarioName, item1.isManualAlert, item1.date),
      },
    ];

    setCompareExecutionsVersions(versions);
  };

  const handleCompareExecutionsClick = (selectedIndexes: number[]) => {
    if (selectedIndexes.length !== 2) return;

    const selectedItems = selectedIndexes.map((index) => filteredData[index]);

    const hasManualAlert = selectedItems.some((item) => item.scenario === "Manual");
    if (hasManualAlert) {
      showErrorToast(
        t("page.case.tab.alerts.and.executions.modal.compare.executions.error.manual.alert")
      );
      return;
    }

    const executions: ScenarioExecutionResultWithCase[] = [];
    for (const item of selectedItems) {
      const execution = executionResultsMap.get(item.id);
      if (execution) {
        executions.push(execution);
      }
    }

    if (executions.length !== 2) {
      return;
    }

    const [firstExecution, secondExecution] = executions;

    const scenarioId1 = firstExecution.scenarioVersion?.scenario?.id;
    const scenarioId2 = secondExecution.scenarioVersion?.scenario?.id;

    if (!scenarioId1 || !scenarioId2 || scenarioId1 !== scenarioId2) {
      showErrorToast(
        t("page.case.tab.alerts.and.executions.modal.compare.executions.error.different.scenarios")
      );
      return;
    }

    setCompareExecutionsModal({
      firstExecution,
      secondExecution,
    });
  };

  return (
    <>
      <div className="full-height">
        <FixedTable
          maxSelectionCount={2}
          id={"executionResultsView"}
          data={filteredData}
          isSelectable
          columns={columns}
          isLoading={false}
          pageSize={defaultPageSize}
          onRowClick={(row) => {
            if (row.scenario === "Manual") {
              navigate(`/alert/${row.id}`);
            } else {
              searchParams.set("executionId", row.id.toString());
              setSearchParams(searchParams);
            }
          }}
          toolbar={(values, { getBatchActionProps }) => (
            <AlertsAndExecutionsToolbar
              setIsDisplayExecutionButton={setIsDisplayExecutionButton}
              values={values}
              alertsAndExecutions={filteredData}
              alertsMap={alertsMap}
              getBatchActionProps={getBatchActionProps}
              onCompareVersions={handleCompareExecutions}
              onCompareExecutions={handleCompareExecutionsClick}
              onlyOpenAlerts={onlyOpenAlerts}
              onOnlyOpenAlertsChange={setOnlyOpenAlerts}
            />
          )}
        />
      </div>

      <ModalTransition>
        {compareExecutionsVersions && compareExecutionsVersions.length > 0 && (
          <CaseVersionsDiffTable
            versions={compareExecutionsVersions}
            title={t("caseVersions.diff.modal.title")}
            subtitle={selectedCase?.request || ""}
            onClose={() => setCompareExecutionsVersions(null)}
            searchInputId="searchExecutionsDiff"
            tableId="caseVersionsDiff"
          />
        )}
      </ModalTransition>

      <ModalTransition>
        {compareExecutionsModal && (
          <CompareExecutionsModal
            firstExecution={compareExecutionsModal.firstExecution}
            secondExecution={compareExecutionsModal.secondExecution}
            subtitle={selectedCase?.request || ""}
            onClose={() => setCompareExecutionsModal(null)}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default AlertsAndExecutionsTab;
